import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import { MAIN_POOL_ID, UIMarket } from '@drift/common';
import { useMemo } from 'react';
import { CollapsibleAccounts, type IFStakeItem } from './CollapsibleAccounts';
import Divider from 'src/components/primitives/Divider/Divider';
import { BN, SpotMarketConfig } from '@drift-labs/sdk';
import { useAllIfData } from 'src/hooks/query/ifData/useAllIfData';
import { DRIFT_TOKEN_BANK_INDEX } from 'src/environmentVariables/EnvironmentVariables';
import useAccountData from 'src/hooks/useAccountData';
import { useVaultDepositorAccounts } from 'src/hooks/query/vaults/base/useVaultDepositorAccounts';

export const Accounts = () => {
	const subAccounts = useTickedAllSubAccounts();
	const currentUserKey = useAccountData()?.userKey;
	const { data: vaultDepositorAccounts } = useVaultDepositorAccounts();
	const vaultsWithBalance = Object.values(vaultDepositorAccounts ?? {}).filter(
		(vaultAccount) => vaultAccount.vaultDepositor.vaultShares.gt(new BN(0))
	);

	const { data: ifDataLookup } = useAllIfData();

	const ifStakesWithBalance = useMemo(() => {
		return Object.entries(ifDataLookup)
			.filter(([_, ifData]) => {
				const currentStake = ifData.userStake.currentStake ?? 0;
				return currentStake > 0;
			})
			.map(([marketIndex, ifData]): IFStakeItem => {
				const market = UIMarket.createSpotMarket(Number(marketIndex));
				return {
					marketIndex: Number(marketIndex),
					ifData,
					poolId: (market.market as SpotMarketConfig).poolId,
					market,
				};
			});
	}, [ifDataLookup]);

	const [driftStake, nonDriftIfStakes] = useMemo(() => {
		const drift: IFStakeItem[] = [];
		const nonDrift: IFStakeItem[] = [];

		ifStakesWithBalance.forEach((item) => {
			if (item.marketIndex === DRIFT_TOKEN_BANK_INDEX) {
				drift.push(item);
			} else {
				nonDrift.push(item);
			}
		});

		return [drift, nonDrift];
	}, [ifStakesWithBalance]);

	const [
		mainPoolSubaccounts,
		isolatedPoolSubaccounts,
		delegatedAccounts,
		currentActivePool,
	] = useMemo(() => {
		const mainPool: typeof subAccounts = [];
		const isolatedPool: typeof subAccounts = [];
		const delegated: typeof subAccounts = [];
		let activePool: 'main' | 'isolated' | 'delegated' | null = null;

		for (const subAccount of subAccounts) {
			if (subAccount.isDelegatedTo) {
				delegated.push(subAccount);
				if (subAccount.userKey === currentUserKey) {
					activePool = 'delegated';
				}
			} else if (subAccount?.client?.getUserAccount().poolId === MAIN_POOL_ID) {
				mainPool.push(subAccount);
				if (subAccount.userKey === currentUserKey) {
					activePool = 'main';
				}
			} else {
				isolatedPool.push(subAccount);
				if (subAccount.userKey === currentUserKey) {
					activePool = 'isolated';
				}
			}
		}

		return [mainPool, isolatedPool, delegated, activePool];
	}, [subAccounts, currentUserKey]);

	const hasMainPoolSubaccounts = mainPoolSubaccounts.length > 0;
	const hasIsolatedPoolSubaccounts = isolatedPoolSubaccounts.length > 0;
	const hasDelegatedAccounts = delegatedAccounts.length > 0;
	const hasVaultsWithBalance = vaultsWithBalance.length > 0;
	const hasDriftStake = driftStake.length > 0;
	const hasIFStakesWithBalance = nonDriftIfStakes.length > 0;
	const hasAnyVaultOrStaking =
		hasVaultsWithBalance || hasDriftStake || hasIFStakesWithBalance;

	const showChildrenDirectly =
		hasMainPoolSubaccounts &&
		!hasIsolatedPoolSubaccounts &&
		!hasDelegatedAccounts;

	const hasNoAccounts = subAccounts.length === 0;

	if (hasNoAccounts && !hasAnyVaultOrStaking) {
		return (
			<div className="flex h-full w-full items-center justify-center text-center">
				You don&apos;t have any funds yet.
				<br />
				Deposit assets to use as collateral and enjoy Earning APY.
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-y-md">
			{!hasNoAccounts && (
				<span className="typo-b4 text-text-secondary">Accounts</span>
			)}
			{hasMainPoolSubaccounts && (
				<CollapsibleAccounts
					title={
						mainPoolSubaccounts.length > 1
							? `Main Accounts (${mainPoolSubaccounts.length})`
							: 'Main Accounts'
					}
					items={mainPoolSubaccounts}
					showChildrenDirectly={showChildrenDirectly}
					highlighted={currentActivePool === 'main'}
				/>
			)}
			{hasIsolatedPoolSubaccounts && (
				<CollapsibleAccounts
					title={
						isolatedPoolSubaccounts.length > 1
							? `Isolated Pool Accounts (${isolatedPoolSubaccounts.length})`
							: 'Isolated Pool Accounts'
					}
					items={isolatedPoolSubaccounts}
					highlighted={currentActivePool === 'isolated'}
				/>
			)}
			{hasDelegatedAccounts && (
				<CollapsibleAccounts
					title={
						delegatedAccounts.length > 1
							? `Managed Accounts (${delegatedAccounts.length})`
							: 'Managed Accounts'
					}
					items={delegatedAccounts}
					highlighted={currentActivePool === 'delegated'}
				/>
			)}
			{hasAnyVaultOrStaking && (
				<>
					{!hasNoAccounts && <Divider />}
					<span className="typo-b4 text-text-secondary">
						Vault/Staking Balances
					</span>
				</>
			)}
			{hasVaultsWithBalance && (
				<CollapsibleAccounts
					title={
						vaultsWithBalance.length > 1
							? `Strategy Vault (${vaultsWithBalance.length})`
							: 'Strategy Vault'
					}
					items={vaultsWithBalance}
				/>
			)}
			{hasDriftStake && (
				<CollapsibleAccounts title={'DRIFT Staking'} items={driftStake} />
			)}
			{hasIFStakesWithBalance && (
				<CollapsibleAccounts
					title={
						nonDriftIfStakes.length > 1
							? `Insurance Fund Vaults (${nonDriftIfStakes.length})`
							: 'Insurance Fund Vaults'
					}
					items={nonDriftIfStakes}
				/>
			)}
		</div>
	);
};
